import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { dict, defaultLang, supported, detectBrowserLang, languageMeta } from "@/lib/i18n";

const I18nContext = createContext(null);
const STORAGE_KEY = "gp_lang";

function readInitialLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && supported.includes(saved)) return saved;
  } catch {
    // localStorage may be unavailable (SSR / private mode)
  }
  return detectBrowserLang() || defaultLang;
}

/**
 * Interpolate placeholders like "{n}" in a translation string.
 */
function interpolate(template, params) {
  if (!params) return template;
  return String(template).replace(/\{(\w+)\}/g, (_, key) =>
    params[key] !== undefined ? params[key] : `{${key}}`
  );
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);
  // Track whether the user has actively picked a language (vs auto-detected).
  const userSelected = useRef(!!(typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)));

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
  }, [lang]);

  // Manual setter — used by <Select>, Settings language picker, etc.
  const setLang = useCallback((l) => {
    if (!supported.includes(l)) return;
    userSelected.current = true;
    setLangState(l);
  }, []);

  /**
   * Apply the server-side preference for the logged-in user. Called by
   * AuthContext (or any auth-aware code) after login/refresh. Server value
   * wins only if the user hasn't manually overridden it during this session.
   */
  const applyServerLang = useCallback((l) => {
    if (!supported.includes(l)) return;
    if (userSelected.current) return; // user overrides win
    setLangState(l);
  }, []);

  const t = useCallback(
    (k, params) => {
      const table = dict[lang] || dict[defaultLang];
      const fallback = dict[defaultLang];
      const value = (table && table[k]) ?? (fallback && fallback[k]) ?? k;
      return interpolate(value, params);
    },
    [lang]
  );

  const localized = useCallback(
    (obj) =>
      (obj &&
        (obj[lang] ||
          obj[defaultLang] ||
          obj.en ||
          obj.de ||
          obj.tr)) ||
      "",
    [lang]
  );

  /**
   * Convenience helper for legacy inline conditionals. Prefer `t(key)`.
   * Usage: tri({ en: "Refresh", de: "Aktualisieren", tr: "Yenile" })
   * Returns the value for the current language, with EN → DE → TR fallback.
   */
  const tri = useCallback(
    (map) => {
      if (!map) return "";
      return map[lang] || map[defaultLang] || map.en || map.de || map.tr || "";
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, applyServerLang, t, tri, localized, supported, languageMeta }),
    [lang, setLang, applyServerLang, t, tri, localized]
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
